var _sentryCollisionFreeGlobalObject = typeof window != "undefined" ? window : typeof global != "undefined" ? global : typeof self != "undefined" ? self : {};
_sentryCollisionFreeGlobalObject["__sentryRewritesTunnelPath__"] = undefined;
_sentryCollisionFreeGlobalObject["SENTRY_RELEASE"] = {"id":"25.0.38"};
_sentryCollisionFreeGlobalObject["__sentryBasePath"] = undefined;
_sentryCollisionFreeGlobalObject["__rewriteFramesAssetPrefixPath__"] = "";

// Sentry client-side configuration
import * as Sentry from '@sentry/nextjs';

import {
  ALLOW_URLS,
  bumpBudget,
  CAPTURE_CONSOLE,
  CLIENT_IGNORE_ERRORS,
  COMMON_CONFIG,
  DENY_URLS,
  isMessageOnlyEvent,
  isPaymentMonitoringEvent,
  isProd,
  SENTRY_ENABLED,
  shouldDropNoisyLoadOrConsoleEvents,
  shouldSampleEvent,
  underBudget,
} from './sentry.config';
import {
  defaultFlags,
  parseThirdPartyFlagsFromCookie,
  parseThirdPartyFlagsFromSearch,
  shouldDisableSentry,
} from './src/lib/thirdPartyFlags';

let isSentryDisabledAtStartup = false;

// Disable Sentry Replay iframe recording — rrweb observing cross-origin iframe
// documents throws on WebKit/iOS Safari. Noibu #49221. No supported Sentry 7.x
// API disables iframe recording (this internal rrweb flag is the only lever in
// 7.120.4); revisit on the Sentry 8 upgrade.
if (typeof window !== 'undefined') {
  (
    window as Window & { __RRWEB_EXCLUDE_IFRAME__?: boolean }
  ).__RRWEB_EXCLUDE_IFRAME__ = true;
}

// Eagerly seed window.__DISABLE_SENTRY__ before Sentry.init() so no events fire
// during the hydration window (before React mounts and ThirdPartyFlagProvider's
// useEffect runs). Resolve with the same precedence as _app/the provider: URL
// params override the session cookie per-key (so an explicit `disable_sentry=0`
// re-enables Sentry even while the cookie still holds the flag), and any param
// absent from the URL inherits the cookie.
if (typeof window !== 'undefined') {
  try {
    const cookieFlags = parseThirdPartyFlagsFromCookie(
      document.cookie,
      defaultFlags,
    );
    const flags = parseThirdPartyFlagsFromSearch(
      window.location.search,
      cookieFlags,
    );
    if (shouldDisableSentry(flags)) {
      isSentryDisabledAtStartup = true;
      (
        window as Window & { __DISABLE_SENTRY__?: boolean }
      ).__DISABLE_SENTRY__ = true;
    }
  } catch {
    // Defensive: never let startup flag resolution break Sentry init.
  }
}

// Client-side specific configuration
Sentry.init({
  ...COMMON_CONFIG,
  enabled:
    SENTRY_ENABLED && !!COMMON_CONFIG.dsn && !isSentryDisabledAtStartup,

  // Payment monitoring is never sampled; other errors are sampled in beforeSend.
  sampleRate: 1,

  integrations: [
    Sentry.reportingObserverIntegration(),
    Sentry.linkedErrorsIntegration(),
    Sentry.dedupeIntegration(),
    ...(CAPTURE_CONSOLE
      ? [Sentry.captureConsoleIntegration({ levels: ['error'] })]
      : []),
    // Browser-only integration (this file only executes in browser)
    Sentry.browserTracingIntegration({
      tracePropagationTargets: [
        /^\/api\//,
        /^https?:\/\/(?!gtm\.)[^/]*breitling\.com/i,
        /^https?:\/\/[^/]*gallet\.com/i,
      ],
    }),
  ],

  // Performance sampling rates - adjust based on environment
  tracesSampleRate: isProd ? 0.05 : 1.0,
  profilesSampleRate: isProd ? 0.05 : 1.0,

  // Additional sampling configuration
  replaysSessionSampleRate: isProd ? 0.01 : 0.1,
  replaysOnErrorSampleRate: isProd ? 0.1 : 1.0,

  // Filter by origin code
  allowUrls: ALLOW_URLS,

  ignoreErrors: CLIENT_IGNORE_ERRORS,
  denyUrls: DENY_URLS,

  sendDefaultPii: false, // Client-side: do not send PII

  beforeBreadcrumb(b: Sentry.Breadcrumb): Sentry.Breadcrumb | null {
    if (b.category === 'navigation') return null;
    return b;
  },

  beforeSend(event: Sentry.Event, hint: Sentry.EventHint): Sentry.Event | null {
    // Drop all events when disable_sentry=1 or disable_third_parties=1 is active.
    if (globalThis.window?.__DISABLE_SENTRY__) return null;

    const isPaymentEvent = isPaymentMonitoringEvent(event);

    if (!isPaymentEvent && !shouldSampleEvent(event)) {
      return null;
    }

    // Per-minute / per-session guard — never throttle payment monitoring.
    if (!isPaymentEvent && !underBudget()) return null;

    if (isMessageOnlyEvent(event) && !CAPTURE_CONSOLE) return null;

    // Filter noisy load/console events (includes double firewall check)
    if (shouldDropNoisyLoadOrConsoleEvents(event, hint, CLIENT_IGNORE_ERRORS)) {
      return null;
    }

    if (!isPaymentEvent) {
      bumpBudget();
    }
    return event;
  },

  beforeSendTransaction(event: Sentry.Event): Sentry.Event | null {
    if (globalThis.window?.__DISABLE_SENTRY__) return null;
    return event;
  },
});

// Replay records only a small sample of sessions, so its code is fetched after the page has loaded.
if (
  typeof window !== 'undefined' &&
  SENTRY_ENABLED &&
  !!COMMON_CONFIG.dsn &&
  !isSentryDisabledAtStartup
) {
  const addReplay = () => {
    import('./src/lib/sentryReplay')
      .then(({ replayIntegration }) =>
        Sentry.addIntegration(
          replayIntegration({ maskAllText: true, blockAllMedia: true }),
        ),
      )
      .catch(() => undefined);
  };

  if (document.readyState === 'complete') {
    addReplay();
  } else {
    window.addEventListener('load', addReplay, { once: true });
  }
}
